import React, { useEffect, useMemo, useState } from 'react';
import GoalsJourneyPath from '../GoalsCard/GoalsJourneyPath';
import {
    getBreakpointId,
    getDefaultPathSamples,
    getDefaultPoints,
} from '../GoalsCard/journeyPointsConfig';
import './GoalsLockedPreviewCard.css';

const GOALS_JOURNEY_BG = `${
    process.env.PUBLIC_URL || ''
}/portal-v2/dashboard/goals-mountain-journey.png`;

const BENEFIT_CHIPS = [
    {
        id: 'top7',
        label: 'Top 7 Goals',
        icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                    d="M4 15l4-4 4 4 8-10"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
    },
    {
        id: 'oola-one',
        label: 'Oola One',
        icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                    d="M5 20V9l7-4 7 4v11"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                />
                <path
                    d="M9 20v-6h6v6"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                />
            </svg>
        ),
    },
    {
        id: 'daily',
        label: 'Daily Action Plan',
        icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
                <path
                    d="M12 8v4l2.5 2.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                />
            </svg>
        ),
    },
];

function LockBadge() {
    return (
        <div className="dv2-goals-lock__badge" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect
                    x="5"
                    y="11"
                    width="14"
                    height="10"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
                <path
                    d="M8 11V8a4 4 0 0 1 8 0v3"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                />
            </svg>
        </div>
    );
}

function PreviewMarkers({ points }) {
    return (
        <ul className="dv2-goals-lock__markers" aria-hidden>
            {(points || []).map((pt) => {
                const isOolaOne = pt.type === 'oolaOne' || pt.rank === 1;
                return (
                    <li
                        key={pt.rank}
                        className={`dv2-goals-lock__marker${
                            isOolaOne ? ' is-oola-one' : ''
                        }`}
                        style={{
                            left: `${pt.x}%`,
                            top: `${pt.y}%`,
                        }}
                    >
                        <span className="dv2-goals-lock__marker-dot">
                            {isOolaOne ? (
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M5 20V9l7-4 7 4v11"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            ) : (
                                <span>{pt.rank}</span>
                            )}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * V2-only locked My Goals preview — aspirational journey teaser for non-subscribers.
 * Does not modify GoalsCard or classic (V1) UI.
 */
const GoalsLockedPreviewCard = ({ upgradeUrl = '#' }) => {
    const [breakpointId, setBreakpointId] = useState(() =>
        typeof window !== 'undefined'
            ? getBreakpointId(window.innerWidth)
            : 'desktop'
    );

    useEffect(() => {
        const update = () => setBreakpointId(getBreakpointId(window.innerWidth));
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    const journeyPoints = useMemo(
        () => getDefaultPoints(breakpointId),
        [breakpointId]
    );
    const pathSamples = useMemo(
        () => getDefaultPathSamples(breakpointId),
        [breakpointId]
    );

    return (
        <section
            className="dv2-goals-lock dv2-glass"
            aria-labelledby="dv2-goals-lock-title"
        >
            <div className="dv2-goals-lock__scene" aria-hidden>
                <div
                    className="dv2-goals-lock__photo"
                    style={{ backgroundImage: `url(${GOALS_JOURNEY_BG})` }}
                />
                <div className="dv2-goals-lock__veil" />
                <div className="dv2-goals-lock__vignette" />
                <div className="dv2-goals-lock__journey">
                    <GoalsJourneyPath
                        pathSamples={pathSamples}
                        stops={journeyPoints}
                    />
                    <PreviewMarkers points={journeyPoints} />
                </div>
            </div>

            <div className="dv2-goals-lock__content">
                <p className="dv2-goals-lock__eyebrow">My Goals</p>
                <LockBadge />
                <h2 id="dv2-goals-lock-title" className="dv2-goals-lock__title">
                    Unlock your Top 7 Goals
                </h2>
                <p className="dv2-goals-lock__body">
                    Create your Top 7, choose your Oola One, and turn your vision
                    into daily action.
                </p>
                <ul className="dv2-goals-lock__chips">
                    {BENEFIT_CHIPS.map((chip) => (
                        <li key={chip.id} className="dv2-goals-lock__chip">
                            <span className="dv2-goals-lock__chip-icon">
                                {chip.icon}
                            </span>
                            {chip.label}
                        </li>
                    ))}
                </ul>
                <a
                    className="dv2-goals-lock__cta"
                    href={upgradeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Upgrade to unlock →
                </a>
            </div>
        </section>
    );
};

export default GoalsLockedPreviewCard;
